import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  tagType?: 'person' | 'place' | 'thing' | 'all';
  onTagTypeChange?: (type: 'person' | 'place' | 'thing' | 'all') => void;
}

interface Tag {
  tag: string;
  type: 'person' | 'place' | 'thing';
  count: number;
}

export default function SearchBar({ value, onChange, onClear, tagType = 'all', onTagTypeChange }: SearchBarProps) {
  const [inputValue, setInputValue] = useState(value);
  const [showFilter, setShowFilter] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Tag[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const filterRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch all tags for autocomplete
  const { data: tagsData } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
  });

  const allTags: Tag[] = tagsData?.tags || [];

  // Sync inputValue with value prop when it changes from parent
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Update suggestions when input changes
  useEffect(() => {
    if (inputValue.trim() && allTags.length > 0) {
      const filtered = allTags
        .filter(tag => {
          // Filter by tag type if not 'all'
          if (tagType !== 'all' && tag.type !== tagType) {
            return false;
          }
          // Filter by input value (case-insensitive)
          return tag.tag.toLowerCase().includes(inputValue.toLowerCase());
        })
        .slice(0, 10) // Limit to top 10 suggestions
        .sort((a, b) => b.count - a.count); // Sort by count (most used first)
      
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  }, [inputValue, tagsData?.tags, tagType]);

  // Close filter/suggestions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilter(false);
      }
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    if (showFilter || showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter, showSuggestions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < filteredSuggestions.length) {
      // User selected a suggestion with Enter key
      const selected = filteredSuggestions[selectedSuggestionIndex];
      setInputValue(selected.tag);
      onChange(selected.tag);
      setShowSuggestions(false);
    } else {
      onChange(inputValue);
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onClear();
    setShowSuggestions(false);
  };

  const handleTagTypeClick = (type: 'person' | 'place' | 'thing' | 'all') => {
    onTagTypeChange?.(type);
    setShowFilter(false);
  };

  const handleSuggestionClick = (tag: Tag) => {
    setInputValue(tag.tag);
    onChange(tag.tag);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const getTagTypeLabel = () => {
    switch (tagType) {
      case 'person':
        return '👤 Person';
      case 'place':
        return '📍 Place';
      case 'thing':
        return '🎯 Thing';
      default:
        return 'All Types';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative z-40">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by tag..."
            className="w-full px-4 py-2.5 sm:py-3 pl-9 sm:pl-10 pr-32 sm:pr-40 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-500 hover:border-gray-400 transition-colors"
            autoComplete="off"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          
          {/* Autocomplete Suggestions Dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-64 overflow-y-auto"
            >
              {filteredSuggestions.map((tag, index) => {
                const getTagColor = () => {
                  switch (tag.type) {
                    case 'person': return 'bg-blue-50 text-blue-900 border-blue-200';
                    case 'place': return 'bg-green-50 text-green-900 border-green-200';
                    case 'thing': return 'bg-purple-50 text-purple-900 border-purple-200';
                  }
                };
                const getCountColor = () => {
                  switch (tag.type) {
                    case 'person': return 'bg-blue-200 text-blue-700';
                    case 'place': return 'bg-green-200 text-green-700';
                    case 'thing': return 'bg-purple-200 text-purple-700';
                  }
                };
                
                return (
                  <button
                    key={`${tag.tag}-${tag.type}`}
                    type="button"
                    onClick={() => handleSuggestionClick(tag)}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 transition-colors ${
                      index === selectedSuggestionIndex 
                        ? 'bg-primary-100' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-900">{tag.tag}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getTagColor()}`}>
                        {tag.type}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getCountColor()}`}>
                        {tag.count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-28 sm:right-36 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Filter Button */}
          <div ref={filterRef} className="absolute right-2 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={() => setShowFilter(!showFilter)}
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg border-2 transition-colors ${
                tagType !== 'all'
                  ? 'bg-primary-100 border-primary-600 text-primary-800 shadow-sm'
                  : 'bg-white border-gray-400 text-gray-800 hover:bg-gray-50 hover:border-gray-500'
              }`}
            >
              {getTagTypeLabel()}
              <svg className="inline-block ml-1 w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Filter Dropdown */}
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-slide-down">
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => handleTagTypeClick('all')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      tagType === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    All Types
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTagTypeClick('person')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      tagType === 'person' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    👤 Person
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTagTypeClick('place')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      tagType === 'place' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    📍 Place
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTagTypeClick('thing')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      tagType === 'thing' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    🎯 Thing
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          type="submit"
          className="btn-primary px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base"
        >
          Search
        </button>
      </div>
    </form>
  );
}

